import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/alert-dialog/alert-dialog';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import { deleteProductMutationOptions } from '@/features/products/api/delete-product.api';
import { GET_LIST_PRODUCT_QUERY_KEY } from '@/features/products/api/get-list-product.api';

export interface DeleteProductDialogProps extends BaseDialogProps {
  productId: string;
  productTitle: string;
}

export default function DeleteProductDialog({
  dialog,
  productId,
  productTitle,
}: DeleteProductDialogProps) {
  const { closeDeleteProductDialog } = useV2DialogContext();
  const queryClient = useQueryClient();

  const deleteProductMutation = useMutation({
    ...deleteProductMutationOptions(),
    onSuccess: () => {
      toast.success('Produk berhasil dihapus');
      queryClient.invalidateQueries({ queryKey: [GET_LIST_PRODUCT_QUERY_KEY] });
      closeDeleteProductDialog();
    },
    onError: error => {
      const message =
        error instanceof AxiosError
          ? ((error.response?.data as { message?: string })?.message ??
            error.message)
          : 'Gagal menghapus produk';
      toast.error(message);
    },
  });

  const isPending = deleteProductMutation.isPending;

  return (
    <AlertDialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState && !isPending) {
          closeDeleteProductDialog();
        }
      }}
    >
      <AlertDialogContent showCloseButton={false}>
        <AlertDialogHeader>
          <AlertDialogTitle>Hapus produk ini?</AlertDialogTitle>
          <AlertDialogDescription>
            Tindakan ini tidak dapat dibatalkan. Produk "{productTitle}" akan
            dihapus secara permanen dari sistem.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Batal</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={isPending}
            onClick={() =>
              deleteProductMutation.mutate({ product_id: productId })
            }
          >
            {isPending ? 'Menghapus...' : 'Hapus'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
